class ProductZoomModal {
  constructor(options = {}) {
    this.modal = null;
    this.isOpen = false;
    this.zoomImage = null;
    
    this.config = {
      modalClass: 'product-zoom-modal',
      overlayClass: 'zoom-modal-overlay',
      contentClass: 'zoom-modal-content',
      closeClass: 'zoom-modal-close',
      imageClass: 'zoom-modal-image',
      ...options
    };

    this.bindEvents();
  }

  bindEvents() {
    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  getDetailImage() {
    // Try to get the metafield value from the product page
    // Method 1: Look for a meta tag or data attribute
    const metaElement = document.querySelector('meta[name="product-detail-image"]');
    if (metaElement) {
      return metaElement.content;
    }

    // Method 2: Look for a script tag with product data
    const productScript = document.querySelector('script[type="application/json"][data-product-json]');
    if (productScript) {
      try {
        const productData = JSON.parse(productScript.textContent);
        if (productData.metafields && productData.metafields.custom && productData.metafields.custom.detail_bild) {
          return productData.metafields.custom.detail_bild;
        }
      } catch (error) {
        console.error('Error parsing product data:', error);
      }
    }

    // Method 3: Look for a data attribute on the product form or container
    const productContainer = document.querySelector('[data-detail-image]');
    if (productContainer) {
      return productContainer.dataset.detailImage;
    }

    // Method 4: Check global window variables (common in Shopify themes)
    if (window.product && window.product.metafields && window.product.metafields.custom && window.product.metafields.custom.detail_bild) {
      return window.product.metafields.custom.detail_bild;
    }

    // Method 5: Check ShopifyAnalytics
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      const product = window.ShopifyAnalytics.meta.product;
      if (product.metafields && product.metafields.custom && product.metafields.custom.detail_bild) {
        return product.metafields.custom.detail_bild;
      }
    }

    console.warn('Detail image metafield (custom.detail_bild) not found');
    return null;
  }

  createModal() {
    if (this.modal) return;

    const detailImageUrl = this.getDetailImage();
    
    if (!detailImageUrl) {
      console.error('No detail image found. Cannot create zoom modal.');
      return;
    }

    // Create modal overlay
    this.modal = document.createElement('div');
    this.modal.className = this.config.modalClass;
    this.modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
      backdrop-filter: blur(4px);
    `;

    // Create close button
    const closeButton = document.createElement('button');
    closeButton.className = this.config.closeClass;
    closeButton.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="18" y1="6" x2="6" y2="18" stroke="white" stroke-width="2" stroke-linecap="round"/>
        <line x1="6" y1="6" x2="18" y2="18" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
    closeButton.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.6);
      border: none;
      border-radius: 50%;
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10001;
      transition: all 0.3s ease;
      backdrop-filter: blur(4px);
      border: 1px solid rgba(255, 255, 255, 0.2);
    `;

    closeButton.addEventListener('mouseenter', () => {
      closeButton.style.background = 'rgba(0, 0, 0, 0.8)';
      closeButton.style.transform = 'scale(1.1)';
    });

    closeButton.addEventListener('mouseleave', () => {
      closeButton.style.background = 'rgba(0, 0, 0, 0.6)';
      closeButton.style.transform = 'scale(1)';
    });

    closeButton.addEventListener('click', () => this.close());

    // Create image container
    const imageContainer = document.createElement('div');
    imageContainer.className = this.config.contentClass;
    imageContainer.style.cssText = `
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Create image element
    this.zoomImage = document.createElement('img');
    this.zoomImage.className = this.config.imageClass;
    this.zoomImage.src = detailImageUrl;
    this.zoomImage.alt = 'Product Detail View';
    this.zoomImage.style.cssText = `
      max-width: 80vw;
      max-height: 80vh;
      object-fit: contain;
      border-radius: 8px;
      box-shadow: 0 10px 50px rgba(0, 0, 0, 0.5);
    `;

    // Add loading state
    this.zoomImage.style.opacity = '0';
    this.zoomImage.addEventListener('load', () => {
      this.zoomImage.style.opacity = '1';
      this.zoomImage.style.transition = 'opacity 0.3s ease';
    });

    this.zoomImage.addEventListener('error', () => {
      console.error('Failed to load detail image');
      this.close();
    });

    // Assemble modal
    imageContainer.appendChild(this.zoomImage);
    this.modal.appendChild(closeButton);
    this.modal.appendChild(imageContainer);

    // Add click-to-close on overlay
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.close();
      }
    });

    // Prevent image click from closing modal
    this.zoomImage.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    document.body.appendChild(this.modal);
  }

  open() {
    this.createModal();
    
    if (!this.modal) return;

    this.isOpen = true;
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Animate in
    requestAnimationFrame(() => {
      this.modal.style.opacity = '1';
      this.modal.style.visibility = 'visible';
    });
  }

  close() {
    if (!this.modal || !this.isOpen) return;

    this.isOpen = false;
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Animate out
    this.modal.style.opacity = '0';
    this.modal.style.visibility = 'hidden';
    
    // Remove from DOM after animation
    setTimeout(() => {
      if (this.modal && this.modal.parentNode) {
        this.modal.parentNode.removeChild(this.modal);
        this.modal = null;
        this.zoomImage = null;
      }
    }, 300);
  }

  destroy() {
    this.close();
  }
}

// Export for use
window.ProductZoomModal = ProductZoomModal;